import i18next from "i18next";

export interface GlossaryEntry {
  /** Display title shown in the hover card header */
  title: string;
  /** Plain-language explanation */
  description: string;
  /** Optional category for grouping on the Glossary page */
  category?: "Lighting hardware" | "Light science" | "Cultivation" | "Controls & software" | "Compliance & certification" | "Business & finance" | "Sustainability";
}

/**
 * Central registry of difficult words and abbreviations used across the site.
 * Keys are matched case-sensitively as whole words by <GlossaryText>.
 * Add new entries here — they will automatically become hoverable wherever
 * <GlossaryText> wraps them.
 */
export const glossary: Record<string, GlossaryEntry> = {
  HPS: {
    title: "High-pressure sodium",
    description:
      "A legacy gas-discharge lamp long used as the standard top light in greenhouses. HPS fixtures emit a broad yellow-orange spectrum at roughly 1.7–1.85 μmol/J — modern LEDs reach 3–4 μmol/J with a tunable spectrum, far less radiated heat, and substantially lower energy use.",
    category: "Lighting hardware",
  },
  LED: {
    title: "Light-emitting diode",
    description:
      "Solid-state light source that converts electricity directly into photons. Horticultural LEDs allow precise spectrum tuning, dimming, and reach efficacies of 3–4 μmol/J — well above legacy HPS — with much lower radiated heat.",
    category: "Lighting hardware",
  },
  PPE: {
    title: "Photosynthetic Photon Efficacy",
    description:
      "How efficiently a fixture converts electrical input into photosynthetically active photons (400–700 nm), expressed in μmol/J. Higher PPE means more usable light per watt — and a lower energy bill for the same light level.",
    category: "Light science",
  },
  PPFD: {
    title: "Photosynthetic Photon Flux Density",
    description:
      "The number of photosynthetically active photons (400–700 nm) hitting a square metre of canopy each second, in μmol/m²/s. Think of it as the instantaneous brightness the crop actually sees.",
    category: "Light science",
  },
  DLI: {
    title: "Daily Light Integral",
    description:
      "Total photosynthetically active light delivered to the crop over 24 hours, in mol/m²/day. Each crop has an optimal DLI window — Synthia™ steers fixtures and dimming to hit it precisely while minimising energy spend.",
    category: "Light science",
  },
  PAR: {
    title: "Photosynthetically Active Radiation",
    description:
      "The 400–700 nm portion of the spectrum that drives photosynthesis. Horticultural lighting metrics like PPFD, DLI and PPE are all measured against PAR rather than total visible light.",
    category: "Light science",
  },
  CEA: {
    title: "Controlled Environment Agriculture",
    description:
      "Cultivation in fully controlled spaces — greenhouses, vertical farms, growth chambers — where light, temperature, humidity, CO₂ and irrigation are actively managed to maximise yield and quality.",
    category: "Cultivation",
  },
  CO2: {
    title: "Carbon dioxide enrichment",
    description:
      "Raising CO₂ above ambient (typically 800–1,200 ppm) accelerates photosynthesis when light, temperature and water are not limiting — a standard tool in modern greenhouse cultivation.",
    category: "Cultivation",
  },
  "μmol/J": {
    title: "Micromoles per joule",
    description:
      "Unit of photon efficacy (PPE). It tells you how many photosynthetically active photons a fixture produces per joule of electrical energy consumed. Higher is better — modern horticultural LEDs reach 3–4 μmol/J versus ~1.7 μmol/J for HPS.",
    category: "Light science",
  },
  "μmol·m⁻²·s⁻¹": {
    title: "Micromoles per square metre per second",
    description:
      "Unit of PPFD — the instantaneous density of photosynthetically active photons (400–700 nm) hitting one square metre of canopy each second. Typical greenhouse top-lighting targets are 150–400 μmol·m⁻²·s⁻¹; vertical farms often run 200–300.",
    category: "Light science",
  },
  "mol/m²/day": {
    title: "Moles per square metre per day",
    description:
      "Unit of Daily Light Integral (DLI) — the total photosynthetically active light delivered to a square metre of crop over 24 hours. Leafy greens typically need 12–17 mol/m²/day; fruiting crops 20–30+.",
    category: "Light science",
  },
  Spectrum: {
    title: "Light spectrum",
    description:
      "The mix of wavelengths a fixture emits. Plants respond differently to blue (compactness, vegetative growth), red (flowering, photosynthesis efficiency), far-red (stem elongation, flowering signals) and UV (secondary metabolites). Tunable LEDs let growers shape the recipe per crop and growth stage.",
    category: "Lighting hardware",
  },
  Photoperiod: {
    title: "Photoperiod",
    description:
      "The number of hours of light a crop receives in a 24-hour cycle. Many crops use photoperiod as a flowering signal — short-day, long-day or day-neutral — and Synthia™ schedules fixtures to hit it precisely.",
    category: "Cultivation",
  },
  Transpiration: {
    title: "Transpiration",
    description:
      "The movement of water from roots through the plant and out via stomata in the leaves. It drives nutrient uptake and cools the canopy. Light, temperature, humidity (VPD) and CO₂ together determine transpiration rate.",
    category: "Cultivation",
  },
  VPD: {
    title: "Vapour Pressure Deficit",
    description:
      "The difference between how much moisture the air holds and how much it could hold at saturation, in kPa. VPD drives transpiration; most crops sit happiest at 0.8–1.2 kPa. Lighting choices affect leaf temperature and therefore VPD.",
    category: "Cultivation",
  },
  Modbus: {
    title: "Modbus",
    description:
      "A widely used industrial communication protocol (originally serial, today mostly Modbus TCP over Ethernet) for talking to PLCs, climate computers and field devices. Photosynthetic fixtures expose Modbus so they integrate cleanly with existing greenhouse and SCADA stacks.",
    category: "Controls & software",
  },
  REST: {
    title: "REST (Representational State Transfer)",
    description:
      "An architectural style for web APIs that uses standard HTTP verbs (GET, POST, PUT, DELETE) against URL-addressed resources, typically exchanging JSON. Synthia™ exposes a documented REST API for recipes, schedules, sensors and fleet management.",
    category: "Controls & software",
  },
  API: {
    title: "Application Programming Interface",
    description:
      "A defined set of endpoints another system can call to read or change data. Synthia™ ships an HTTP/JSON API so growers can integrate fixtures with climate computers, ERPs, BI tools and custom automations.",
    category: "Controls & software",
  },
  DMX: {
    title: "DMX512",
    description:
      "A lighting control protocol originating in stage and entertainment lighting, now common in horticulture for low-latency dimming and channel control. Photosynthetic fixtures support DMX alongside 0–10 V, Modbus and the Synthia™ API.",
    category: "Controls & software",
  },
  SCADA: {
    title: "Supervisory Control and Data Acquisition",
    description:
      "Software systems that monitor and control industrial processes — common in greenhouses for orchestrating climate, irrigation, lighting and energy. Photosynthetic integrates with SCADA stacks via Modbus and REST.",
    category: "Controls & software",
  },
  PLC: {
    title: "Programmable Logic Controller",
    description:
      "Industrial microcontrollers used to automate machinery and processes. In greenhouses they orchestrate climate, irrigation and lighting; Photosynthetic fixtures interoperate with PLCs via Modbus and 0–10 V.",
    category: "Controls & software",
  },
  JSON: {
    title: "JavaScript Object Notation",
    description:
      "A lightweight, human-readable text format for structured data. Synthia™'s REST API exchanges JSON for recipes, schedules, telemetry and fleet operations.",
    category: "Controls & software",
  },
  DALI: {
    title: "Digital Addressable Lighting Interface",
    description:
      "An open, two-wire digital protocol for controlling individual light fixtures by address — dim, group, scene and query each one without rewiring. Common in commercial and horticultural lighting alongside 0–10 V, DMX and Modbus.",
    category: "Controls & software",
  },
  ROI: {
    title: "Return on Investment",
    description:
      "How quickly a purchase pays for itself in saved costs or added revenue. For LED retrofits the typical drivers are lower kWh consumption, higher yields and reduced HVAC load — Synthia™ models all three to estimate payback time per site.",
    category: "Business & finance",
  },
  L90B10: {
    title: "L90B10 lifetime rating",
    description:
      "A standard LED lifetime metric: the number of operating hours after which 90% of a fixture's photon output remains (L90), and no more than 10% of units have failed (B10). Photosynthetic fixtures are rated L90B10 well beyond 50,000 hours.",
    category: "Compliance & certification",
  },
  UL: {
    title: "UL listing (United States)",
    description:
      "Independent safety certification by Underwriters Laboratories, required or expected for electrical equipment sold in the US and Canada. Confirms a product meets recognised safety standards for fire, shock and hazard.",
    category: "Compliance & certification",
  },
  UKCA: {
    title: "UK Conformity Assessed",
    description:
      "The United Kingdom's product-conformity mark for goods placed on the British market post-Brexit. Functionally equivalent to CE for most electrical products.",
    category: "Compliance & certification",
  },
  CE: {
    title: "Conformité Européenne",
    description:
      "The European Economic Area's conformity mark — the manufacturer declares the product meets EU safety, health and environmental requirements. Mandatory for selling electrical equipment in the EU/EEA.",
    category: "Compliance & certification",
  },
  PSE: {
    title: "PSE mark (Japan)",
    description:
      "Japan's mandatory product-safety certification for electrical appliances under the DENAN law. Required to import or sell mains-powered equipment in the Japanese market.",
    category: "Compliance & certification",
  },
  RoHS: {
    title: "Restriction of Hazardous Substances",
    description:
      "An EU directive (mirrored in many other markets) limiting the use of lead, mercury, cadmium and other hazardous materials in electrical equipment. Photosynthetic fixtures are RoHS-compliant.",
    category: "Compliance & certification",
  },
  "daisy-chain": {
    title: "Daisy-chain wiring",
    description:
      "A wiring scheme where one fixture's power or data output feeds the next, and so on down the line — drastically reducing cabling, conduit and labour in dense rack and greenhouse layouts.",
    category: "Lighting hardware",
  },
  "hot-swappable": {
    title: "Hot-swappable",
    description:
      "A module that can be removed and replaced while the rest of the system stays powered and running. In Photosynthetic fixtures, drivers and LED modules are hot-swappable so a single failure never takes a row offline.",
    category: "Lighting hardware",
  },
  "photometric layout": {
    title: "Photometric layout",
    description:
      "A computer-modelled lighting plan that simulates how photons from each fixture distribute across the canopy — used to dimension a project for the right PPFD, uniformity and DLI before any hardware is installed.",
    category: "Lighting hardware",
  },
  telemetry: {
    title: "Telemetry",
    description:
      "Continuous data streamed from each fixture and sensor — power draw, temperature, runtime, faults, environmental readings — back to Synthia™ for monitoring, alerting and analytics.",
    category: "Controls & software",
  },
  "adaptive recipe": {
    title: "Adaptive lighting recipe",
    description:
      "A spectrum and intensity schedule that adjusts itself in real time based on sensor data, sunlight, growth stage and energy prices — instead of running a fixed dim curve. Synthia™ closes the loop using PPFD, climate and tariff inputs.",
    category: "Controls & software",
  },
  "remote diagnostics": {
    title: "Remote diagnostics",
    description:
      "Inspecting fixture health — drivers, LEDs, controllers, network — from anywhere via Synthia™, without sending a technician on site. Most issues can be triaged and many resolved before the grower notices.",
    category: "Controls & software",
  },
  HVAC: {
    title: "Heating, Ventilation and Air Conditioning",
    description:
      "The systems that condition air inside a greenhouse or vertical farm — temperature, humidity and airflow. LEDs radiate far less heat than HPS, which typically reduces HVAC sizing, energy use and dehumidification load.",
    category: "Cultivation",
  },
  CAGR: {
    title: "Compound Annual Growth Rate",
    description:
      "The smoothed yearly growth rate that takes a value from a starting point to an ending point over a number of years, as if it grew at the same rate each year. A standard way to compare growth across markets and time horizons.",
    category: "Business & finance",
  },
  OPEX: {
    title: "Operating Expenditure",
    description:
      "Day-to-day running costs — electricity, labour, maintenance, consumables. Higher-efficacy LEDs and adaptive recipes lower OPEX by cutting kWh and HVAC load over the life of the installation.",
    category: "Business & finance",
  },
  CAPEX: {
    title: "Capital Expenditure",
    description:
      "Upfront investment in long-lived assets — fixtures, controllers, infrastructure. Photosynthetic projects are evaluated on combined CAPEX + OPEX over the fixture lifetime, not sticker price alone.",
    category: "Business & finance",
  },
  DLC: {
    title: "DesignLights Consortium",
    description:
      "A North American non-profit whose Qualified Products List for horticultural lighting is widely used as the eligibility benchmark for utility rebate programmes. DLC-listed fixtures meet defined photon efficacy, output and warranty thresholds.",
    category: "Compliance & certification",
  },
  IP65: {
    title: "Ingress Protection 65",
    description:
      "An international enclosure-rating standard (IEC 60529). The first digit (6) means the fixture is completely dust-tight; the second digit (5) means it is protected against low-pressure water jets from any direction. IP65 is the minimum rating expected for greenhouse and vertical-farm luminaires exposed to humidity, irrigation mist and regular wash-downs.",
    category: "Compliance & certification",
  },
  ESG: {
    title: "Environmental, Social and Governance",
    description:
      "A framework investors and customers use to assess a company's non-financial performance — environmental footprint, social impact and governance practices. Photosynthetic reports on energy savings, supply-chain ethics and board oversight under this lens.",
    category: "Sustainability",
  },
  SDG: {
    title: "Sustainable Development Goals",
    description:
      "The United Nations' 17 goals adopted in 2015 to guide global development to 2030 — covering food security, clean energy, sustainable cities and more. Horticultural LEDs map most directly to SDG 2 (Zero Hunger), 7 (Affordable & Clean Energy) and 12 (Responsible Consumption).",
    category: "Sustainability",
  },
  KPI: {
    title: "Key Performance Indicator",
    description:
      "A measurable value tracked to judge how well an objective is being met — for a grower, things like yield per m², kWh per kg of produce, uptime and DLI hit-rate. Synthia™ surfaces these as live dashboards per site and per fleet.",
    category: "Business & finance",
  },
  "few-shot learning": {
    title: "Few-shot learning",
    description:
      "A machine-learning approach where a model adapts to a new task from only a handful of examples, rather than thousands. Synthia™ uses few-shot techniques so a new crop or cultivar can be optimised after a short initial run, not a full season of data.",
    category: "Controls & software",
  },
  "Bio-convergence": {
    title: "Bio-convergence",
    description:
      "The convergence of biology, engineering, data science and AI to design living systems and the environments around them. In horticulture it means combining plant physiology, sensors, lighting hardware and machine learning into a single closed-loop platform — the basis of Photosynthetic's research with NMBU.",
    category: "Light science",
  },
  Photobiomodulation: {
    title: "Photobiomodulation",
    description:
      "The use of specific wavelengths of red and near-infrared light to influence biological processes at the cellular level — most studied in human and animal tissue, where it is linked to mitochondrial activity and recovery. Photosynthetic studies its plant-side analogues to better understand how non-photosynthetic wavelengths shape growth and stress response.",
    category: "Light science",
  },
  UVC: {
    title: "UVC (ultraviolet-C)",
    description:
      "The 200–280 nm short-wavelength ultraviolet band. UVC photons are energetic enough to damage microbial DNA, which makes UVC LEDs effective for surface, water, and aerosol disinfection. In horticulture and CEA, UVC is studied for pathogen and airborne-microbe control rather than for driving photosynthesis.",
    category: "Light science",
  },
  shadowing: {
    title: "Fixture shadowing",
    description:
      "The shadow a luminaire casts on the canopy below it, which blocks ambient or solar light from reaching the crop. Narrow-profile bars and well-spaced mounting reduce shadowing and protect natural DLI in greenhouses where supplemental LEDs work alongside daylight.",
    category: "Lighting hardware",
  },
  RH: {
    title: "Relative humidity",
    description:
      "The amount of water vapour in the air expressed as a percentage of the maximum the air could hold at that temperature. RH directly affects transpiration, nutrient uptake, and disease pressure in CEA — most crops perform best in the 60–80 % range.",
    category: "Cultivation",
  },
  RS485: {
    title: "RS-485",
    description:
      "A robust two-wire serial communication standard used to connect lighting fixtures, sensors, and climate computers over long cable runs. RS-485 supports multi-drop networks — meaning hundreds of devices can share one bus — making it the backbone of most horticultural lighting control systems.",
    category: "Controls & software",
  },
  PPF: {
    title: "Photosynthetic Photon Flux",
    description:
      "The total number of photosynthetically active photons (400–700 nm) a fixture emits per second, in μmol/s. PPF describes the luminaire's total photon output — not how much actually reaches the canopy (that's PPFD).",
    category: "Light science",
  },
  Photon: {
    title: "Photon",
    description:
      "A single quantum of light. Plants count photons, not lumens — which is why horticultural lighting is measured in micromoles (μmol) of photons rather than human-eye-weighted lux.",
    category: "Light science",
  },
  Micromole: {
    title: "Micromole (μmol)",
    description:
      "A unit of quantity used for photons in horticultural lighting. One micromole equals roughly 6.02 × 10¹⁷ photons — the practical unit behind PPF (μmol/s), PPFD (μmol/m²/s) and DLI (mol/m²/day).",
    category: "Light science",
  },
  Uniformity: {
    title: "Light uniformity",
    description:
      "How evenly photons are distributed across the grow area, expressed as the ratio of minimum to average PPFD. High uniformity means every plant receives a similar light dose, which translates directly into more even growth and predictable yields.",
    category: "Lighting hardware",
  },
  Photomorphogenesis: {
    title: "Photomorphogenesis",
    description:
      "Light-driven plant development — germination, branching, stem elongation, flowering — triggered by photoreceptors such as phytochromes (red / far-red) and cryptochromes (blue). Tunable LED spectra let growers steer morphology without changing the climate.",
    category: "Cultivation",
  },
  "secondary metabolite": {
    title: "Secondary metabolite",
    description:
      "Plant compounds that aren't required for basic growth but drive flavour, aroma, colour and health benefits — antioxidants, carotenoids, anthocyanins, essential oils and cannabinoids. Spectrum, intensity and stress can all shift secondary-metabolite production.",
    category: "Cultivation",
  },
  "full spectrum": {
    title: "Full spectrum",
    description:
      "A horticultural luminaire that emits broad white light spanning blue, green, yellow and red — visually similar to sunlight. There is no single industry standard for what counts as 'full spectrum', so always check the spectral distribution rather than the marketing label.",
    category: "Lighting hardware",
  },
  "TIR optics": {
    title: "Total internal reflection optics",
    description:
      "A reflectorless lens technology that uses total internal reflection to shape the beam, delivering a stable and uniform photon distribution onto the canopy with minimal loss compared with traditional reflectors.",
    category: "Lighting hardware",
  },
  "far-red": {
    title: "Far-red light (700–800 nm)",
    description:
      "The band just past visible red. Far-red drives stem elongation, leaf expansion and flowering in many crops via the phytochrome system, and increases photosynthesis when added to red and blue light (the Emerson enhancement effect).",
    category: "Light science",
  },
  "passive cooling": {
    title: "Passive cooling",
    description:
      "Fanless thermal management that dissipates heat through housing geometry and conduction alone. With no moving parts, passively cooled fixtures avoid fan failures and dust ingress — a major reliability and maintenance advantage in greenhouses and grow rooms.",
    category: "Lighting hardware",
  },
  "resin encapsulation": {
    title: "Resin encapsulation",
    description:
      "A manufacturing technique where electronics are sealed under a food-safe, chemically inert polyurethane resin. The result is a fully ingress-protected, maintenance-free luminaire that tolerates wash-downs, humidity and aggressive indoor-farm conditions.",
    category: "Lighting hardware",
  },
  PBAR: {
    title: "Photobiologically Active Radiation",
    description:
      "The extended 300–800 nm spectrum that affects plants in ways beyond photosynthesis — including UV (defence compounds, morphology) and far-red (flowering, leaf expansion). PBAR captures effects that fall outside the narrower 400–700 nm PAR window.",
    category: "Light science",
  },
  IK: {
    title: "Impact protection (IK) rating",
    description:
      "An IEC standard (EN 62262) scoring a luminaire's resistance to mechanical impact from IK00 to IK10, where higher numbers indicate more robust enclosures. Relevant for fixtures installed in busy greenhouse rows or rack systems where bumps and tools are routine.",
    category: "Compliance & certification",
  },
  "Grow area": {
    title: "Grow area",
    description:
      "The floor footprint dedicated to crop production under the lights. In a greenhouse it's the lit zone beneath the canopy; in a vertical farm it's the footprint of a single rack layer — total canopy area equals footprint × number of layers.",
    category: "Cultivation",
  },
  "Truss height": {
    title: "Truss / ceiling height",
    description:
      "Distance from the floor to the underside of the greenhouse truss (or to the ceiling of a vertical-farm rack). It sets the maximum mounting height for fixtures, and together with crop height determines fixture-to-canopy clearance, beam spread and PPFD uniformity.",
    category: "Cultivation",
  },
  "Crop height": {
    title: "Crop height",
    description:
      "Typical height of the mature crop from substrate to the top of the canopy. Used with truss height to calculate fixture-to-canopy clearance — too little clearance creates hot spots and shadowing; too much wastes photons in spill light.",
    category: "Cultivation",
  },
  PPS: {
    title: "Pre-production sample",
    description:
      "An early sample batch produced on the intended manufacturing line before full volume production. Used to verify build quality, tolerances, performance and assembly process — once the PPS is approved, the first volume order is released.",
    category: "Lighting hardware",
  },
  EPEX: {
    title: "EPEX SPOT",
    description:
      "European Power Exchange — the main day-ahead and intraday wholesale electricity market across continental Europe and the UK. Hourly spot prices from EPEX are the reference signal Synthia™ uses to shift supplemental lighting toward cheaper, lower-carbon hours.",
    category: "Business & finance",
  },
  PV: {
    title: "Photovoltaic",
    description:
      "Solar panels that convert sunlight directly into electricity. On-site PV pairs naturally with greenhouse lighting controls: Synthia™ can lean on dimming when PV output is high and ramp back up when the array drops off.",
    category: "Sustainability",
  },
  DSO: {
    title: "Distribution System Operator",
    description:
      "The regulated utility that operates the local electricity distribution grid and sets connection capacity, tariffs and curtailment rules. DSO constraints often cap how much supplemental lighting a site can run simultaneously — adaptive dimming keeps loads within the contracted envelope.",
    category: "Business & finance",
  },
  "A/B test": {
    title: "A/B test",
    description:
      "A controlled trial in which two cultivation zones run different light recipes (spectrum, intensity or photoperiod) while all other inputs are held equal. Synthia™ uses closed-loop A/B tests to validate recipe changes against yield and quality before rolling them out fleet-wide.",
    category: "Controls & software",
  },
  CSRD: {
    title: "Corporate Sustainability Reporting Directive",
    description:
      "EU regulation requiring large companies to disclose detailed environmental, social and governance data — including energy use and Scope 1–3 emissions — under the ESRS standards. Fixture-level energy and DLI telemetry from Synthia™ feeds directly into CSRD-ready reporting.",
    category: "Compliance & certification",
  },
};

/** Ordered list of terms, longest first, used to build the matching regex. */
export const glossaryTerms = Object.keys(glossary).sort((a, b) => b.length - a.length);

/**
 * Resolve a glossary entry against the active i18n language, falling back to
 * the English source defined above. Categories stay in English (translated
 * for display via `glossaryPage.categories.<name>` in GlossaryPage).
 */
export const getTranslatedEntry = (key: string): GlossaryEntry | undefined => {
  const base = glossary[key];
  if (!base) return undefined;
  const lang = i18next.language || "en";
  if (lang.startsWith("en")) return base;
  const bundle = i18next.getResource(lang, "translation", "glossaryEntries") as
    | Record<string, { title?: string; description?: string }>
    | undefined;
  const localized = bundle?.[key];
  return {
    ...base,
    title: localized?.title || base.title,
    description: localized?.description || base.description,
  };
};